import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Send, Beaker } from "lucide-react";
import { toast } from "sonner";

const KIND_COLOR = {
  otp: "bg-blue-100 text-blue-800",
  invoice: "bg-green-100 text-green-800",
  booking: "bg-purple-100 text-purple-800",
  reminder: "bg-orange-100 text-orange-800",
  template_test: "bg-amber-100 text-amber-800",
  marketing: "bg-pink-100 text-pink-800",
  generic: "bg-gray-100 text-gray-800",
};

export default function WhatsAppLogs() {
  const [logs, setLogs] = useState([]);
  const [mode, setMode] = useState("mock");
  const [testOpen, setTestOpen] = useState(false);

  const load = () => {
    api.get("/whatsapp/logs").then(r => setLogs(r.data));
    api.get("/whatsapp/status").then(r => setMode(r.data.mode));
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-5" data-testid="whatsapp-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Comms</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">WhatsApp Logs</h1>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={mode === "live" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"} data-testid="wa-mode">
            {mode.toUpperCase()} mode
          </Badge>
          <Button onClick={() => setTestOpen(true)} data-testid="send-test-btn"><Beaker className="w-4 h-4 mr-1" />Send test invoice</Button>
        </div>
      </div>

      {mode === "mock" && (
        <Card className="p-4 bg-yellow-50/50 border-yellow-200">
          <div className="text-sm">Wabis credentials not set. Messages are logged here only. Open <strong>Settings → Wabis</strong> to paste your API key and go live.</div>
        </Card>
      )}

      <Card className="p-4">
        <div className="divide-y divide-border">
          {logs.map(l => (
            <div key={l.id} className="py-3" data-testid={`wa-log-${l.id}`}>
              <div className="flex items-center gap-2">
                <Badge className={KIND_COLOR[l.kind] || ""}>{l.kind}</Badge>
                <span className="font-mono text-sm">{l.to}</span>
                <span className="text-xs text-muted-foreground ml-auto">{new Date(l.created_at).toLocaleString()}</span>
                <Badge variant="outline" className="text-[10px]">{l.status}</Badge>
                <Badge variant="outline" className="text-[10px]">{l.mode}</Badge>
              </div>
              <div className="mt-2 text-sm whitespace-pre-line bg-muted rounded-md p-2 font-mono text-xs">{l.body}</div>
              {l.meta?.template_name && (
                <div className="mt-1 text-[10px] text-muted-foreground">template: <code>{l.meta.template_name}</code> · params: {JSON.stringify(l.meta.params || [])}</div>
              )}
              {l.meta?.template_fallback_reason && (
                <div className="mt-1 text-[10px] text-rose-700 bg-rose-50 px-2 py-1 rounded">⚠ Template send failed: {l.meta.template_fallback_reason} → fell back to plain text.</div>
              )}
            </div>
          ))}
          {logs.length === 0 && <div className="text-sm text-muted-foreground py-6">No messages yet.</div>}
        </div>
      </Card>

      <SendTestDialog open={testOpen} onOpenChange={setTestOpen} onSent={load} />
    </div>
  );
}

function SendTestDialog({ open, onOpenChange, onSent }) {
  const [mobile, setMobile] = useState("");
  const [useReal, setUseReal] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invoiceId, setInvoiceId] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setMobile(""); setUseReal(false); setInvoiceId(""); setResult(null);
      api.get("/invoices?limit=20").then(r => setInvoices(r.data || [])).catch(() => {});
    }
  }, [open]);

  const send = async () => {
    if (!mobile || mobile.length < 10) { toast.error("Enter a valid 10-digit mobile"); return; }
    setBusy(true);
    try {
      const params = { test_recipient: mobile };
      if (useReal && invoiceId) params.invoice_id = invoiceId;
      const { data } = await api.post("/settings/wabis/test-invoice-template", null, { params });
      setResult(data);
      if (data.status === "sent" || data.status === "mock") {
        toast.success(data.status === "mock" ? "Logged (mock mode)" : "Template sent! Check WhatsApp.");
        onSent && onSent();
      } else {
        toast.error(`Send failed: ${data.meta?.error || "see logs"}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="send-test-dialog">
        <DialogHeader><DialogTitle>Send test invoice WhatsApp</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground bg-amber-50 border border-amber-200 rounded p-2.5">
            Sends the formatted invoice message (header image + items + total + invoice link + Google review link) via Wabis.
            Configure the header image and review link in <strong>Settings → Invoice WhatsApp message</strong>.
          </div>
          <div>
            <Label className="text-xs">Recipient mobile (with country code or 10-digit Indian)</Label>
            <Input value={mobile} onChange={e => setMobile(e.target.value)} placeholder="9000123456" data-testid="test-mobile" autoFocus />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={useReal} onChange={e => setUseReal(e.target.checked)} data-testid="test-use-real" />
            Use a real invoice (otherwise dummy data is sent)
          </label>
          {useReal && (
            <div>
              <Label className="text-xs">Pick invoice</Label>
              <Select value={invoiceId} onValueChange={setInvoiceId}>
                <SelectTrigger data-testid="test-invoice"><SelectValue placeholder="Select invoice…" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {invoices.map(i => <SelectItem key={i.id} value={i.id}>{i.invoice_no} · ₹{i.grand_total?.toFixed(2)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {result && (
            <div className="text-xs bg-muted rounded p-2.5 font-mono space-y-1 max-h-72 overflow-auto">
              <div>Status: <strong>{result.status}</strong> · Mode: {result.mode}</div>
              {result.meta?.endpoint_used && (
                <div className="text-emerald-700">✓ Endpoint used: {result.meta.endpoint_used}</div>
              )}
              {result.meta?.wa_message_id && (
                <div className="text-emerald-700">✓ wa_message_id: {result.meta.wa_message_id}</div>
              )}
              {result.meta?.template_fallback_reason && (
                <div className="text-rose-700">⚠ Fallback reason: {result.meta.template_fallback_reason}</div>
              )}
              {result.meta?.template_attempts && result.meta.template_attempts.length > 0 && (
                <details className="mt-1">
                  <summary className="cursor-pointer text-rose-700">Show Wabis raw responses ({result.meta.template_attempts.length})</summary>
                  <pre className="mt-1 whitespace-pre-wrap break-all">
                    {JSON.stringify(result.meta.template_attempts, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={send} disabled={busy} data-testid="test-send-btn"><Send className="w-4 h-4 mr-1" />{busy ? "Sending…" : "Send test"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
